import React, { useState } from 'react';
import type { User } from '@octery/shared';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';
import { Link } from '@/app/router';
import { useCurrentPath, navigate } from '@/app/navigation';
import { Button } from '@/components/ui/button';

const navItems = [
  { href: '/', label: 'Dashboard', icon: '📊' },
  { href: '/conversations', label: 'Conversations', icon: '💬' },
  { href: '/knowledge-base', label: 'Knowledge Base', icon: '📚' },
  { href: '/ai-settings', label: 'AI Settings', icon: '🤖' },
  { href: '/settings', label: 'Settings', icon: '⚙️' },
  { href: '/partners', label: 'Partners', icon: '🤝', adminOnly: true },
];

function SidebarContent({
  path,
  user,
  onLogout,
  onNavClick,
}: {
  path: string;
  user: Pick<User, 'name' | 'email' | 'role' | 'isSystemAdmin'> | null;
  onLogout: () => void;
  onNavClick?: () => void;
}) {
  const isAdmin = user?.isSystemAdmin ?? false;
  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <>
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Octery" className="w-8 h-8" />
          <div>
            <h1 className="text-xl font-bold text-primary leading-none">Octery</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Admin Panel</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const isActive =
            item.href === '/' ? path === '/' : path.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
              onClick={onNavClick}
            >
              <span>{item.icon}</span>
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-medium">
            {user?.name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full" onClick={onLogout}>
          Sign out
        </Button>
      </div>
    </>
  );
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const path = useCurrentPath();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Ignore error — logout anyway
    }
    logout();
    navigate('/login');
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — desktop: always visible, mobile: slide-in */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 border-r bg-card flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:static lg:translate-x-0 lg:shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent
          path={path}
          user={user}
          onLogout={handleLogout}
          onNavClick={() => setSidebarOpen(false)}
        />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden border-b bg-card px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-md hover:bg-accent"
            aria-label="Open menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
          </button>
          <img src="/logo.png" alt="Octery" className="w-6 h-6" />
          <h1 className="text-lg font-bold text-primary">Octery</h1>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
